import React from "react";
import { useTranslation } from "react-i18next";
import { useSettings } from "@/context/SettingsContext";
import { telHref, mailHref, whatsappHref, socialHandle, propertyMapsUrl } from "@/lib/helpers";
import { track } from "@/api/analytics";
import * as Icons from "lucide-react";

/**
 * A tappable row for a single contact channel.
 * `accent="bronze"` (default) matches the boutique palette.
 * `accent="whatsapp"` uses the official brand green — the only non-bronze
 * accent allowed in the app, reserved for the primary channel of the property.
 */
function ContactRow({ icon: Icon, label, value, href, onClick, testid, accent = "neutral" }) {
  if (!value) return null;
  const styles = {
    neutral: {
      card: "border-ink-200/40 bg-white/95",
      iconBox: "bg-cream-100",
      iconColor: "text-bronze-500",
    },
    bronze: {
      card: "border-bronze-300/70 bg-cream-50",
      iconBox: "bg-bronze-100",
      iconColor: "text-bronze-500",
    },
    whatsapp: {
      card: "border-[#25D366]/40 bg-[#25D366]/[0.06]",
      iconBox: "bg-[#25D366]/15",
      iconColor: "text-[#128C7E]",
    },
  }[accent];

  return (
    <a
      href={href}
      onClick={onClick}
      target={href?.startsWith("http") ? "_blank" : undefined}
      rel="noreferrer"
      data-testid={testid}
      className={`flex items-center gap-3 rounded-2xl border ${styles.card} px-4 py-4 hover:bg-cream-100 active:scale-[0.99] transition`}
    >
      <div className={`w-11 h-11 rounded-xl ${styles.iconBox} flex items-center justify-center`}>
        <Icon strokeWidth={1.4} className={`w-5 h-5 ${styles.iconColor}`} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-ink-300">{label}</div>
        <div className="text-sm font-semibold text-ink-900 truncate">{value}</div>
      </div>
      <Icons.ArrowUpRight strokeWidth={1.4} className="w-4 h-4 text-ink-300 shrink-0" />
    </a>
  );
}

function SocialButton({ icon: Icon, url, testid }) {
  const handle = socialHandle(url);
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      data-testid={testid}
      className="flex flex-col items-center gap-1.5 group max-w-[130px]"
    >
      <span className="w-12 h-12 rounded-full bg-white border border-ink-200/40 flex items-center justify-center group-hover:border-bronze-300 group-hover:bg-cream-100 transition">
        <Icon strokeWidth={1.4} className="w-5 h-5 text-bronze-500" />
      </span>
      {handle && (
        <span className="text-[10px] text-ink-500 group-hover:text-bronze-500 transition tracking-tight truncate max-w-full">
          {handle}
        </span>
      )}
    </a>
  );
}

export default function Contacts() {
  const { t } = useTranslation();
  const { settings } = useSettings();
  if (!settings) return null;

  const waNumber = settings.contact_whatsapp || settings.contact_phone;
  const waHref = whatsappHref(waNumber);
  // If the guest has only one contact number (no dedicated WhatsApp), we skip the
  // redundant "Telefono" card — WhatsApp already exposes the same number as its primary action.
  const showPhoneRow = settings.contact_phone && settings.contact_phone !== waNumber;

  return (
    <div className="px-5 pt-6 pb-8">
      <div className="text-center animate-fade-up">
        <div className="mx-auto w-12 h-12 rounded-2xl bg-bronze-50 flex items-center justify-center">
          <Icons.PhoneCall strokeWidth={1.4} className="w-5 h-5 text-bronze-500" />
        </div>
        <h1 data-testid="contacts-title" className="font-editorial italic text-[38px] text-ink-900 mt-3">
          {t("contacts.title")}
        </h1>
        <p
          data-testid="contacts-tagline"
          className="font-editorial italic text-[15px] text-ink-500 mt-1"
        >
          {t("tagline.contacts")}
        </p>
        <p className="text-[11px] text-ink-300 mt-0.5">{t("contacts.helper")}</p>
        <div className="flex justify-center my-3"><span className="ornament" /></div>
      </div>

      <div className="mt-3 flex flex-col gap-3">
        <ContactRow
          icon={Icons.MessageCircle}
          label={t("contacts.whatsapp")}
          value={waNumber}
          href={waHref}
          onClick={() => track("click_whatsapp", { target_type: "property" })}
          testid="contacts-whatsapp"
          accent="whatsapp"
        />
        {showPhoneRow && (
          <ContactRow
            icon={Icons.Phone}
            label={t("contacts.phone")}
            value={settings.contact_phone}
            href={telHref(settings.contact_phone)}
            onClick={() => track("click_phone", { target_type: "property" })}
            testid="contacts-phone"
          />
        )}
        <ContactRow
          icon={Icons.Mail}
          label={t("contacts.email")}
          value={settings.contact_email}
          href={mailHref(settings.contact_email)}
          onClick={() => track("click_email", { target_type: "property" })}
          testid="contacts-email"
        />
        <ContactRow
          icon={Icons.MapPin}
          label={t("contacts.address")}
          value={settings.contact_address}
          href={propertyMapsUrl(settings)}
          onClick={() => track("click_maps", { target_type: "property" })}
          testid="contacts-address"
        />
        <ContactRow
          icon={Icons.Globe}
          label={t("contacts.website")}
          value={settings.contact_website}
          href={settings.contact_website}
          onClick={() => track("click_website", { target_type: "property" })}
          testid="contacts-website"
        />
      </div>

      {(settings.social_facebook || settings.social_instagram) && (
        <section className="mt-6 animate-fade-up">
          <div className="text-[10px] text-center font-semibold uppercase tracking-[0.2em] text-bronze-500 mb-3">
            {t("contacts.social")}
          </div>
          <div className="flex justify-center gap-8">
            {settings.social_facebook && (
              <SocialButton icon={Icons.Facebook} url={settings.social_facebook} testid="contacts-facebook" />
            )}
            {settings.social_instagram && (
              <SocialButton icon={Icons.Instagram} url={settings.social_instagram} testid="contacts-instagram" />
            )}
          </div>
        </section>
      )}
    </div>
  );
}
